import EventApi from '@telia-oa/eventapi-client';

import { IAnalyticsObject } from 'telia-front-react';
import config from '../config';
import { InvoiceDeliveryType, InvoiceSettings } from '../stores/models/CustomerInvoicesSettings';
import { ISsoStore } from '../stores/SsoStore';

import { ContentType } from '../stores/models/ContentType';
import { formatAsMonthAndYear } from '../util/DateUtil';

declare let dataLayer;

const UNPAID_INVOICES = 'Tasumata arved';
const INVOICE_SETTINGS = 'Perioodiliste arvete seaded';
const PREVIOUS_INVOICES = 'Varasemad arved';
const PAYMENT_HISTORY = 'Maksete ajalugu';
const PAYMENT_MODAL = 'Maksmise modaal';
const INVOICE_DETAILS_MODAL = 'Vaatan arvet modaal';
const POS_PAYMENT_MODAL = 'Kassa maksmise modaal';

export type GaPageLabel = 'invoices' | 'invoices groups' | 'direct payment' | 'payment history' | 'unify invoices' | 'create payment plan';

const INVOICE_SETTINGS_FIELD_LABELS = {
  LANGUAGE_ET: 'language - estonian',
  LANGUAGE_RU: 'language - russian',
  LANGUAGE_EN: 'language - english',
  TITLE_CHANGE: 'invoice title',
  DELIVERY_SELFSERVICE: 'delivery channel - self service',
  DELIVERY_BANK: 'delivery channel - e-invoice bank',
  DELIVERY_EMAIL: 'delivery channel - email',
  DELIVERY_POST: 'delivery channel - post',
  DELIVERY_INVOICE_CENTRAL: 'delivery channel - invoice central',
  DELIVERY_MOBILE: 'delivery channel - mobile',
  EMAIL_PDF: 'email file type - PDF',
  EMAIL_XML: 'email file type - XML',
  MULTIPLE_EMAILS: 'two email addresses',
  EMAIL_CHANGE: 'email field',
  ADDRESS_CHANGE: 'billing address',
  IBAN_CHANGE: 'bank account',
  INVOICE_CENTRAL_ADDRESS_CHANGE: 'invoice central account',
  MOBILE_CHANGE: 'mobile number',
  RECIPIENT_CLIENT: 'recipient - same as client',
  RECIPIENT_OTHER: 'recipient - different as client'
};

export type InvoiceSettingsField =
  | 'LANGUAGE_ET'
  | 'LANGUAGE_RU'
  | 'LANGUAGE_EN'
  | 'TITLE_CHANGE'
  | 'DELIVERY_SELFSERVICE'
  | 'DELIVERY_BANK'
  | 'DELIVERY_EMAIL'
  | 'DELIVERY_POST'
  | 'DELIVERY_INVOICE_CENTRAL'
  | 'DELIVERY_MOBILE'
  | 'EMAIL_PDF'
  | 'EMAIL_XML'
  | 'EMAIL_CHANGE'
  | 'MULTIPLE_EMAILS'
  | 'ADDRESS_CHANGE'
  | 'IBAN_CHANGE'
  | 'INVOICE_CENTRAL_ADDRESS_CHANGE'
  | 'MOBILE_CHANGE'
  | 'RECIPIENT_CLIENT'
  | 'RECIPIENT_OTHER';

export const onGaPush = (data: IAnalyticsObject): void => {
  dataLayer?.push({
    event: data.event,
    eventCategory: data.page,
    eventLabel: data.id,
    eventAction: data.group
  });
};

export default class AnalyticsService {
  private oaCrmWebApi;

  constructor(private ssoStore: ISsoStore) {
    this.oaCrmWebApi = new EventApi(config.oaCrmEnvironmentName, config.oaCrmApplicationName, () => this.ssoStore.customer.customerId.toString());
  }

  public ctaGetElectronicInvoice(): void {
    AnalyticsService.gaEvent(INVOICE_SETTINGS, 'Üleskutse - Loobun paberarvest');
  }

  public getInvoiceSettingsSaveChanges(original: InvoiceSettings, updated: InvoiceSettings): string {
    return AnalyticsService.parseChanges(original, updated)
      .map((key) => INVOICE_SETTINGS_FIELD_LABELS[key])
      .join(', ');
  }

  public invoiceSettingsSaveChanges(deliveryType: InvoiceDeliveryType): void {
    AnalyticsService.gaEvent(INVOICE_SETTINGS, `Salvestamine - Saamise viis: ${deliveryType}`);
  }

  public invoiceSettingsViewInvoiceSettings(contentType: ContentType, referenceNumber: string): void {
    this.crmEvent('INFO_BILLS_VIEW_BILL_SETTINGS', {
      BILL_REF_NUM: referenceNumber,
      BILL_TYPE: AnalyticsService.crmBillType(contentType)
    });
  }

  public unpaidInvoicesViewDetails(invoiceType: ContentType, invoiceNumber: string): void {
    AnalyticsService.gaEvent(UNPAID_INVOICES, `${AnalyticsService.invoiceName(invoiceType)} - Vaatan arvet`);
    this.crmEvent('INFO_BILLS_VIEW_SINGLE_BILL', {
      BILL_ID: invoiceNumber,
      BILL_TYPE: this.crmBillType(invoiceType),
      BILL_VIEW_TYPE: 'web'
    });
  }

  public unpaidInvoicesPay(invoiceType: ContentType, invoiceNumber: string): void {
    AnalyticsService.gaEvent(UNPAID_INVOICES, `${AnalyticsService.invoiceName(invoiceType)} - Maksan`);
    this.crmEvent('ACTION_BILLS_PAYMENT_SINGLE_BILL', {
      BILL_ID: invoiceNumber,
      BILL_TYPE: this.crmBillType(invoiceType)
    });
  }

  public unpaidInvoicesPayAll(): void {
    AnalyticsService.gaEvent(UNPAID_INVOICES, 'Maksan kõik arved');
    this.crmEvent('ACTION_BILLS_PAYMENT_ALL_BILLS');
  }

  public unpaidInvoicesMakePrepayment(): void {
    AnalyticsService.gaEvent(UNPAID_INVOICES, 'Teen ettemakse');
    this.crmEvent('ACTION_BILLS_MAKE_ADVANCE_PAYMENT');
  }

  public previousInvoicesViewPaymentHistory(): void {
    AnalyticsService.gaEvent(PREVIOUS_INVOICES, 'Maksete ajalugu');
  }

  public previousInvoicesApplyFilter(): void {
    AnalyticsService.gaEvent(PREVIOUS_INVOICES, 'Filter - Otsi');
  }

  public paymentHistoryDownloadXlsx(): void {
    AnalyticsService.gaEvent(PAYMENT_HISTORY, 'Ekspordi XLSX');
  }

  public paymentHistoryViewTransactionsDetails(): void {
    AnalyticsService.gaEvent(PAYMENT_HISTORY, 'Open - detailvaade');
  }

  public previousInvoicesViewDetails(contentType: ContentType, invoiceNumber: string): void {
    this.crmEvent('INFO_BILLS_VIEW_SINGLE_BILL', {
      BILL_ID: invoiceNumber,
      BILL_TYPE: AnalyticsService.crmBillType(contentType),
      BILL_VIEW_TYPE: 'web'
    });
  }

  public previousInvoicesViewMore(): void {
    AnalyticsService.gaEvent(PREVIOUS_INVOICES, 'Vaatan rohkem');
  }

  public invoiceDetailsPay(invoiceType: ContentType, invoiceNumber: string): void {
    AnalyticsService.gaEvent(`${INVOICE_DETAILS_MODAL} - ${AnalyticsService.invoiceName(invoiceType)}`, 'Maksan');
    this.crmEvent('ACTION_BILLS_PAYMENT_SINGLE_BILL', {
      BILL_ID: invoiceNumber,
      BILL_TYPE: this.crmBillType(invoiceType)
    });
  }

  public paymentModalPayAllInvoices(bankLinkName: string): void {
    AnalyticsService.gaEvent(`${PAYMENT_MODAL} - Arvete tasumine`, bankLinkName);
  }

  public paymentModalPayInvoice(bankLinkName: string): void {
    AnalyticsService.gaEvent(`${PAYMENT_MODAL} - Arve tasumine`, bankLinkName);
  }

  public paymentModalMakePrepayment(bankLinkName: string): void {
    AnalyticsService.gaEvent(`${PAYMENT_MODAL} - Teen ettemakse`, bankLinkName);
  }

  public posPaymentModalMakePayment(): void {
    AnalyticsService.gaEvent(POS_PAYMENT_MODAL, 'Teen makse');
  }

  public paymentHistoryOpenFilter(): void {
    AnalyticsService.gaEvent(PAYMENT_HISTORY, 'Filter');
  }

  public paymentHistoryApplyFilter(fromDate: Date, toDate: Date): void {
    AnalyticsService.gaEvent(PAYMENT_HISTORY, `Uuenda väljavõtet - alates ${formatAsMonthAndYear(fromDate)} - kuni ${formatAsMonthAndYear(toDate)}`);
  }

  public invoicesViewPage(): void {
    this.crmEvent('INFO_BILLS');
  }

  public previousInvoicesViewPage(): void {
    this.crmEvent('INFO_BILLS_VIEW_PAYMENT_HISTORY');
  }

  public invoicesOnPrePaymentDisplayed(): void {
    this.crmEvent('INFO_BILLS_VIEW_ADVANCE_PAYMENT');
  }

  private crmEvent(name: string, content?: unknown): void {
    this.oaCrmWebApi.recordEvent(name, null, content ? content : null);
  }

  private static gaEvent(action: string, label: string): void {
    dataLayer.push({
      event: 'AutoEvent',
      eventCategory: 'Era Arved',
      eventAction: action,
      eventLabel: label
    });
  }

  private static invoiceName(invoiceType: ContentType): string {
    switch (invoiceType) {
      case 'MOBILE':
        return 'Mobiilsed teenused';
      case 'GOODS':
        return 'Kaubaarved';
      case 'STATE_FEES':
        return 'Võlamenetluse riigilõiv';
      case 'FINANCE':
        return 'Järelmaks';
      case 'DEBT':
        return 'Võla sissenõudekulu';
      case 'TELESERVICES':
        return 'Teenus';
      case 'PENALTY':
        return 'Viivis';
      default:
        return 'Diili teenused';
    }
  }

  private crmBillType(invoiceType: ContentType): string {
    switch (invoiceType) {
      case 'MOBILE':
        return 'mob';
      case 'GOODS':
      case 'STATE_FEES':
      case 'FINANCE':
      case 'DEBT':
      case 'TELESERVICES':
      case 'PENALTY':
        return 'dax';
      default:
        return 'telia';
    }
  }

  public static parseChanges(original: InvoiceSettings, updated: InvoiceSettings): InvoiceSettingsField[] {
    const changes: InvoiceSettingsField[] = [];

    if (original.invoiceLanguage !== updated.invoiceLanguage) {
      if (updated.invoiceLanguage === 'et') {
        changes.push('LANGUAGE_ET');
      }
      if (updated.invoiceLanguage === 'ru') {
        changes.push('LANGUAGE_RU');
      }
      if (updated.invoiceLanguage === 'en') {
        changes.push('LANGUAGE_EN');
      }
    }
    if (original.title !== updated.title) {
      changes.push('TITLE_CHANGE');
    }
    if (updated.invoiceDeliveries.includes('EMAIL')) {
      if (!original.invoiceDeliveries.includes('EMAIL')) {
        changes.push('DELIVERY_EMAIL');
      }
      if (!original.invoiceDeliveries.includes('EMAIL') || original.fileTypes.slice().sort().join() !== updated.fileTypes.slice().sort().join()) {
        if (updated.fileTypes.includes('PDF')) {
          changes.push('EMAIL_PDF');
        }
        if (updated.fileTypes.includes('XML')) {
          changes.push('EMAIL_XML');
        }
      }
      if (!original.invoiceDeliveries.includes('EMAIL') || original.emailAddresses.length !== updated.emailAddresses.length) {
        if (updated.emailAddresses.length > 1) {
          changes.push('MULTIPLE_EMAILS');
        }
      }
      if (!original.invoiceDeliveries.includes('EMAIL') || original.emailAddresses.toString() !== updated.emailAddresses.toString()) {
        changes.push('EMAIL_CHANGE');
      }
    }
    if (updated.invoiceDeliveries.includes('POST')) {
      if (!original.invoiceDeliveries.includes('POST')) {
        changes.push('DELIVERY_POST');
      }
      if (!original.invoiceDeliveries.includes('POST') || (updated.address && updated.address.sourceApi === 'RESS_API')) {
        changes.push('ADDRESS_CHANGE');
      }
    }
    if (updated.invoiceDeliveries.includes('MOBILE')) {
      if (!original.invoiceDeliveries.includes('MOBILE')) {
        changes.push('DELIVERY_MOBILE');
      }
      if (!original.invoiceDeliveries.includes('MOBILE') || original.mobileNumber.toString() !== updated.mobileNumber.toString()) {
        changes.push('MOBILE_CHANGE');
      }
    }
    if (updated.invoiceDeliveries.includes('BANK')) {
      if (!original.invoiceDeliveries.includes('BANK')) {
        changes.push('DELIVERY_BANK');
      }
      if (!original.invoiceDeliveries.includes('BANK') || original.ibanOrUserCode !== updated.ibanOrUserCode) {
        changes.push('IBAN_CHANGE');
      }
    }
    if (updated.invoiceDeliveries.includes('INVOICE_CENTRAL')) {
      if (!original.invoiceDeliveries.includes('INVOICE_CENTRAL')) {
        changes.push('DELIVERY_INVOICE_CENTRAL');
      }
      if (!original.invoiceDeliveries.includes('INVOICE_CENTRAL') || original.invoiceCentralAddress !== updated.invoiceCentralAddress) {
        changes.push('INVOICE_CENTRAL_ADDRESS_CHANGE');
      }
    }
    if (updated.invoiceDeliveries.length === 0 && original.invoiceDeliveries.length !== 0) {
      changes.push('DELIVERY_SELFSERVICE');
    }
    if (updated.isRecipientSameAsCustomer && !original.isRecipientSameAsCustomer) {
      changes.push('RECIPIENT_CLIENT');
    }
    if (!updated.isRecipientSameAsCustomer && original.isRecipientSameAsCustomer) {
      changes.push('RECIPIENT_OTHER');
    }

    return changes;
  }

  private static crmBillType(contentType: ContentType): string {
    switch (contentType) {
      case 'MOBILE':
        return 'mob';
      case 'BROADBAND':
        return 'bb';
      case 'PERIODIC_INVOICES':
        return 'telia';
      default:
        return 'dax';
    }
  }
}
